import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Button, IconButton, Paper, Snackbar, TextField, Tooltip, Typography, Grid } from '@mui/material';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks';
import { PhotoDataType } from '../../types/types';
import { usePhotoRequest } from '../../hooks/photoRequest';
import { IMG_BASE_URL } from '../../utils/config';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import AppPagination from '../../components/AppPagination';
import Loading from '../../components/Loading';
import SiteDialog from '../../components/SiteDialog';
import AddNewPhoto from '../../components/photo/AddNewPhoto';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AlertDialog from '../../components/AlertDialog';
import SearchInput from '../../components/filters/SearchInput';
import RefreshButton from '../../components/filters/RefreshButton';

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
});

interface FormData {
  name: string;
}

const ImagesList: React.FC = () => {
  const { search } = useLocation();
  const { loading, data: photosData } = useAppSelector((state) => state.photo);
  const [photos, setPhotos] = useState<PhotoDataType[]>([]);
  const [lastPage, setLastPage] = useState<number>(1);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [photoData, setPhotoData] = useState<PhotoDataType | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [ searchText,setSearchText ] = useState<string>("")
  const { setValue, register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  const photoRequest = usePhotoRequest();

  const fetchData = useCallback(async () => {
    try {
      await photoRequest({ method: 'GET', data: null, id: null });
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  }, [photoRequest]);

  const handleOnSubmit = useCallback(
    async (data: FormData) => {
      if (photoData) {
        const formattedData = { ...photoData, name: data.name };
        const dispatchData = await photoRequest({ method: 'PATCH', data: formattedData, id: Number(formattedData.id) });
        if (dispatchData.meta.requestStatus === 'fulfilled') {
          setPhotoData(null);
          fetchData();
        }
      }
    },
    [photoData, photoRequest, fetchData]
  );

  const handleDelete = useCallback(async () => {
    if (deleteId !== null) {
      try {
        await photoRequest({ method: 'DELETE', data: null, id: deleteId });
        fetchData();
        setShowAlert(false);
      } catch (error) {
        console.error('Failed to delete photo:', error);
      }
    }
  }, [deleteId, fetchData, photoRequest]);

  const handleEdit = useCallback((photo: PhotoDataType) => {
    setPhotoData(photo);
  }, []);

  const handleDeleteClick = useCallback((id: number) => {
    setDeleteId(id);
    setShowAlert(true);
  }, []);

  useEffect(() => {
    if (photosData) {
      const metaData = photosData?.meta;
      setLastPage(metaData?.lastPage || 1);
      setPhotos(photosData?.data);
    }
  }, [photosData]);

  useEffect(() => {
    fetchData();
    setPhotoData(null);
  }, [search, fetchData]);

  const handleCopy = useCallback(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }, []);

  useEffect(() => {
    if (photoData && photoData.name) {
      setValue('name', photoData.name);
    }
  }, [photoData, setValue]);

  const photoList = useMemo(
    () =>
      photos.length > 0 &&
      photos.map((photo: PhotoDataType, index: number) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
          <ImageListItem sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <img
              srcSet={`${IMG_BASE_URL}${photo.link}?w=248&fit=crop&auto=format&dpr=2 2x`}
              src={`${IMG_BASE_URL}${photo.link}?w=248&fit=crop&auto=format`}
              alt={photo.name}
              loading="lazy"
              style={{ width: '100%', height: 'auto' }}
            />
            {photoData && photoData.id === photo.id ? (
              <Box sx={{ mt: 2 }}>
                <form
                  autoComplete="off"
                  onSubmit={handleSubmit(handleOnSubmit)}
                  style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}
                >
                  <TextField
                    autoFocus
                    sx={{ mr: 1 }}
                    {...register('name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name ? errors.name.message : ''}
                  />
                  <Button type="submit" variant="contained">
                    Update
                  </Button>
                </form>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <ImageListItemBar title={<Typography sx={{ mt: 2 }}>{photo.name}</Typography>} position="below" />
                <IconButton onClick={() => handleEdit(photo)} color="warning">
                  <ModeEditIcon />
                </IconButton>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
              <CopyToClipboard text={`${photo.filename}`} onCopy={handleCopy}>
                <Tooltip title="Copy link to clipboard" arrow>
                  <Button variant="text" color="primary" startIcon={<ContentCopyIcon />}>
                    Copy Link
                  </Button>
                </Tooltip>
              </CopyToClipboard>
              <IconButton onClick={() => handleDeleteClick(photo.id)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </ImageListItem>
        </Grid>
      )),
    [photos, photoData, handleSubmit, handleOnSubmit, handleEdit, register, errors, handleCopy, handleDeleteClick]
  );

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: '12px' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Photos List
      </Typography>
      {loading && <Loading />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Button variant="contained" color="primary" onClick={() => setCreateOpen(true)}>
          Add New Photo
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <SearchInput
            searchText={searchText}
            setSearchText={setSearchText}
            placeholderText='Search by title'
          />
          <Box sx={{ mr: 3 }}></Box>
          <RefreshButton/>
        </Box>
        <SiteDialog open={createOpen} setOpen={setCreateOpen} dialogChildren={<AddNewPhoto setDialogOpen={setCreateOpen} />} />
      </Box>
      <Grid container spacing={2}>
        {photos.length > 0 ? (
          photoList
        ) : (
          <Grid item xs={12}>
            <Typography align="center">No photos found</Typography>
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <AppPagination pageCount={Number(lastPage)} />
      </Box>
      <Snackbar open={copied} message="Link copied to clipboard!" autoHideDuration={3000} onClose={() => setCopied(false)} />
      <AlertDialog
        toggle={showAlert}
        setToggle={setShowAlert}
        cancel={() => setShowAlert(false)}
        confirm={handleDelete}
        title="Are you sure?"
        content="You want to delete this photo."
        type="error"
      />
    </Paper>
  );
};

export default ImagesList;
